import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';

export type Role = 'Owner' | 'Admin' | 'Editor' | 'Viewer';

const ROLE_RANK: Record<Role, number> = { Viewer: 1, Editor: 2, Admin: 3, Owner: 4 };

export interface AuthResponse {
  token: string;
  expiresAt: string;
  email: string;
  displayName: string | null;
  companyId: number | null;
  companyName: string | null;
}

export interface StoredAuth {
  token: string;
  expiresAt: string;
  email: string;
  displayName: string | null;
  companyId: number | null;
  companyName: string | null;
}

const STORAGE_KEY = 'enfin.auth';

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (b64.length % 4)) % 4;
    const padded = b64 + '='.repeat(pad);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private base = environment.apiBaseUrl;

  private state = signal<StoredAuth | null>(this.loadFromStorage());

  readonly user = computed(() => this.state());
  readonly currentCompanyId = computed(() => this.state()?.companyId ?? null);
  readonly currentCompanyName = computed(() => this.state()?.companyName ?? null);
  readonly isAuthenticated = computed(() => {
    const s = this.state();
    if (!s) return false;
    return new Date(s.expiresAt).getTime() > Date.now();
  });

  readonly role = computed<Role | null>(() => {
    const token = this.state()?.token;
    if (!token) return null;
    const payload = decodeJwtPayload(token);
    const rawClaim = payload?.['role']
      ?? payload?.['Role']
      ?? payload?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    const raw = Array.isArray(rawClaim) ? rawClaim[0] : rawClaim;
    if (typeof raw !== 'string' || raw.length === 0) return null;
    const normalized = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    return (normalized in ROLE_RANK ? normalized as Role : null);
  });

  hasRole(min: Role): boolean {
    const r = this.role();
    if (!r) return false;
    return ROLE_RANK[r] >= ROLE_RANK[min];
  }

  login(email: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.base}/auth/login`, { email, password })
      .pipe(tap(res => this.saveSession(res)));
  }

  register(email: string, password: string, displayName: string | null, companyName: string | null): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.base}/auth/register`, { email, password, displayName, companyName })
      .pipe(tap(res => this.saveSession(res)));
  }

  switchCompany(companyId: number): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.base}/companies/${companyId}/switch`, {})
      .pipe(tap(res => this.saveSession(res)));
  }

  logout(): void {
    localStorage.removeItem(STORAGE_KEY);
    this.state.set(null);
  }

  getToken(): string | null {
    return this.state()?.token ?? null;
  }

  private saveSession(res: AuthResponse): void {
    const stored: StoredAuth = {
      token: res.token,
      expiresAt: res.expiresAt,
      email: res.email,
      displayName: res.displayName,
      companyId: res.companyId,
      companyName: res.companyName
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    this.state.set(stored);
  }

  private loadFromStorage(): StoredAuth | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as StoredAuth;
      if (new Date(parsed.expiresAt).getTime() <= Date.now()) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }
}
